/**
 * Text that reaches the client already worded in English — server errors, the room's system
 * chat lines, commands the host rejected, crash reports of the workers — shown in the interface
 * language. The English catalogue holds every one of those messages (`err.*`, `srv.*`, `chat.*`,
 * `cmd.*`); a message is matched against those templates, its parameters recovered and the
 * sentence re-worded. Unknown messages fall back to a generic line for their error code, else
 * they print as they came.
 */
import { TECHS, type TechId } from '@friction/engine';
import type { MsgKey, Translator } from '../i18n';
import { en } from '../i18n/en';
import { nationFromName } from '../i18n/grammar/de';

interface Template {
  key: MsgKey;
  re: RegExp;
  names: string[];
}

const escapeRe = (s: string): string => s.replace(/[.*+?^$()|[\]\\]/g, '\\$&');

/** Every plain-template message of the families that arrive as English text. */
const TEMPLATES: Template[] = (Object.keys(en) as MsgKey[])
  .filter((k) => /^(err|srv|chat|cmd)\./.test(k) && k !== 'cmd.rejected' && typeof en[k] === 'string')
  .map((key) => {
    const text = en[key] as string;
    const names: string[] = [];
    const body = escapeRe(text).replace(/\\?\{(\w+)\\?\}/g, (_m, name: string) => {
      names.push(name);
      return '(.+?)';
    });
    return { key, re: new RegExp(`^${body}$`), names };
  })
  // Longer templates first, so "Lost connection to the server (…)." wins over a shorter prefix.
  .sort((a, b) => b.re.source.length - a.re.source.length);

const TECH_BY_NAME = new Map<string, TechId>(Object.values(TECHS).map((d) => [d.name, d.id]));

/** A server/engine message in the translator's language. */
export function localizeServerText(message: string, t: Translator, code?: string | null): string {
  if (t.locale === 'en') return message;
  for (const tpl of TEMPLATES) {
    const m = tpl.re.exec(message);
    if (!m) continue;
    const params: Record<string, string> = {};
    tpl.names.forEach((name, i) => {
      const v = m[i + 1];
      if (name === 'tech') params[name] = TECH_BY_NAME.has(v) ? t(`tech.${TECH_BY_NAME.get(v)!}.name`) : v;
      else if (name === 'names')
        params[name] = v
          .split(', ')
          .map((n) => nationFromName(n))
          .join(', ');
      else if (name === 'reason' || name === 'detail') params[name] = localizeServerText(v, t);
      else if (name === 'name' && v === 'A player') params[name] = t('srv.aPlayer');
      else params[name] = v;
    });
    // The key's parameters are exactly the template's placeholders, recovered above.
    return (t as unknown as (key: MsgKey, p: Record<string, string>) => string)(tpl.key, params);
  }
  if (code && `srv.code.${code}` in en) return t(`srv.code.${code}` as MsgKey & `srv.code.${string}`);
  return message;
}
